import React, { useState } from 'react';
import logoIcon from '../assets/logo-icon-exact.png';
import { supabase } from '../supabaseClient';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';

function Login({ darkMode, toggleDark }) {
  const navigate = useNavigate();
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');

  const [showReset, setShowReset]       = useState(false);
  const [resetEmail, setResetEmail]     = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError]     = useState('');
  const [resetSent, setResetSent]       = useState(false);

  const [showPromo, setShowPromo]   = useState(false);
  const [promoCode, setPromoCode]   = useState('');
  const [promoError, setPromoError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setPromoError('');

    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) { setError(signInError.message); setLoading(false); return; }

    const code = promoCode.trim().toUpperCase();
    if (code && data.session?.user?.id) {
      const { data: rpcData, error: rpcError } = await supabase.rpc('redeem_promo_code', {
        code_to_redeem: code,
        user_id_to_set: data.session.user.id,
      });
      if (rpcError || !rpcData?.success) {
        setPromoError(rpcData?.reason || 'Promo code not recognised. Check your code and try again.');
        setLoading(false);
        return;
      }
      window.location.replace('/quiz');
      return;
    }

    setLoading(false);
  };

  const handleResetRequest = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) setResetError(error.message);
    else setResetSent(true);
    setResetLoading(false);
  };

  const backToLogin = () => {
    setShowReset(false);
    setResetEmail('');
    setResetError('');
    setResetSent(false);
  };

  const header = (
    <header>
      <div className="logo" onClick={() => navigate('/')}>
        <div className="logo-icon"><img src={logoIcon} alt="" /></div>
        <div className="logo-text">TradeReady<span>Pro</span></div>
      </div>
      <div className="header-right">
        <Link to="/login" className="dark-toggle">Login</Link>
        <Link to="/quiz" className="dark-toggle">Home</Link>
        <Link to="/subscribe" className="btn-subscribe">Subscribe</Link>
        <Link to="/settings" className="dark-toggle" title="Settings">⚙️</Link>
      </div>
    </header>
  );

  if (showReset) {
    return (
      <div className="auth-page">
        {header}
        <div className="auth-wrap">
          <div className="auth-card">
            {resetSent ? (
              <>
                <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>📬</div>
                <h2>Check Your Email</h2>
                <div className="auth-success" style={{ marginTop: '0.75rem' }}>
                  A password reset link has been sent to <strong>{resetEmail}</strong>.
                </div>
                <p className="auth-sub" style={{ marginTop: '0.5rem' }}>
                  Follow the link in the email to set a new password. Check your spam folder if it doesn't arrive within a few minutes.
                </p>
                <button className="btn-auth" onClick={backToLogin}>← Back to Login</button>
              </>
            ) : (
              <>
                <h2>Reset Password</h2>
                <p className="auth-sub">Enter your account email and we'll send you a reset link.</p>
                {resetError && <div className="auth-error">{resetError}</div>}
                <form onSubmit={handleResetRequest}>
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email" value={resetEmail}
                      onChange={e => setResetEmail(e.target.value)}
                      placeholder="you@example.com" required autoFocus
                    />
                  </div>
                  <button type="submit" className="btn-auth" disabled={resetLoading}>
                    {resetLoading ? 'Sending…' : 'Send Reset Link'}
                  </button>
                </form>
                <div className="auth-footer">
                  <button className="forgot-link" onClick={backToLogin}>← Back to Login</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      {header}
      <div className="auth-wrap">
        <div className="auth-card">
          <h2>Login</h2>
          <p className="auth-sub">Welcome back — pick up where you left off.</p>
          {error && <div className="auth-error">{error}</div>}
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com" required
              />
            </div>
            <div className="form-group">
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Password
                <button
                  type="button" className="forgot-link"
                  onClick={() => { setShowReset(true); setResetEmail(email); }}
                >
                  Forgot your password?
                </button>
              </label>
              <input
                type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Your password" required
              />
            </div>
            <button type="submit" className="btn-auth" disabled={loading}>
              {loading ? 'Signing in…' : '🔒 Login'}
            </button>
          </form>

          <div style={{ marginTop: '1rem', borderTop: '1px solid rgba(12,31,63,0.08)', paddingTop: '1rem' }}>
            <button
              type="button"
              className="forgot-link"
              onClick={() => { setShowPromo(p => !p); setPromoError(''); }}
              style={{ width: '100%', textAlign: 'left' }}
            >
              {showPromo ? '▾ Hide promo code' : '▸ Have a promo code?'}
            </button>
            {showPromo && (
              <div style={{ marginTop: '0.75rem' }}>
                {promoError && <div className="auth-error" style={{ marginBottom: '0.6rem' }}>{promoError}</div>}
                <div className="form-group" style={{ marginBottom: '0.35rem' }}>
                  <input
                    type="text"
                    value={promoCode}
                    onChange={e => { setPromoCode(e.target.value); setPromoError(''); }}
                    placeholder="Enter promo code"
                    style={{ textTransform: 'uppercase' }}
                    autoFocus
                  />
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  Enter your code above, then log in — it will be applied automatically.
                </div>
              </div>
            )}
          </div>

          <div className="auth-footer">
            <strong>First time here?</strong> <Link to="/subscribe">Create an account →</Link>
            <div style={{ marginTop: '0.4rem', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Have a promo code? Create your account first, then enter it on signup.
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Login;
